<template>
  <div>
    <button
      style="display: none"
      type="button"
      class="btn-show-modal-signin"
      data-bs-toggle="modal"
      data-bs-target="#modalSignIn"
    ></button>
    <button
      style="display: none"
      type="button"
      class="btn-hide-modal-signin"
      data-bs-dismiss="modal"
      data-bs-target="#modalSignIn"
    ></button>
    <div
      class="modal fade"
      id="modalSignIn"
      tabindex="-1"
      aria-labelledby="modalSignInLabel"
      aria-hidden="true"
    >
      <KskModalShell :width="680" :show-close="false">
        <div class="ksk-signin">
          <!-- KSK018: keeps the btn-close-modal class — ModalStaffSignIn closes
               itself with a page-wide $(".btn-close-modal").click(). -->
          <button
            type="button"
            class="btn-close-modal ksk-signin__close"
            :aria-label="$t('kiosk.common.close')"
            data-bs-dismiss="modal"
          >
            <span class="material-icons-round">close</span>
          </button>

          <!-- KSK065: number not registered yet — QR to the member sign-up with
               the number filled in; we poll until they have set their PIN. -->
          <template v-if="signUpPhone">
            <div class="ksk-signin__icon">
              <span class="material-icons-round">qr_code_2</span>
            </div>
            <h2 id="modalSignInLabel" class="ksk-signin__title">{{ $t("kiosk.signIn.signUpTitle") }}</h2>
            <p class="ksk-signin__caption">
              {{ $t("kiosk.signIn.signUpCaption", { phone: "+65 " + signUpPhone }) }}
            </p>
            <div class="ksk-signin__qr">
              <VueQRCodeComponent :text="signUpUrl" :size="320" color="#1a1c1a" bg-color="#ffffff" error-level="M" />
            </div>
            <div class="ksk-signin__waiting" role="status">
              <span class="ksk-signin__spinner"></span>
              {{ $t("kiosk.signIn.signUpWaiting") }}
            </div>
            <!-- KSK097: visible 180s countdown; at 0:00 the popup closes. -->
            <div class="ksk-signin__expires" role="timer">
              <span class="material-icons-round">timer</span>
              {{ $t("kiosk.signIn.signUpExpiresIn", { time: signUpClock }) }}
            </div>
            <KskButton
              class="ksk-signin__submit"
              variant="outlined"
              size="md"
              block
              icon="arrow_back"
              @click="stopSignUp()"
            >{{ $t("kiosk.signIn.signUpBack") }}</KskButton>
            <button type="button" class="ksk-signin__guest" data-bs-dismiss="modal" @click="continueAsGuest">
              {{ $t("kiosk.signIn.continueGuest") }}
            </button>
          </template>
          <template v-else>
          <div class="ksk-signin__icon">
            <span class="material-icons-round">person_outline</span>
          </div>
          <h2 id="modalSignInLabel" class="ksk-signin__title">{{ $t("kiosk.signIn.title") }}</h2>
          <p class="ksk-signin__caption">
            {{ $t("kiosk.signIn.caption") }}
          </p>

          <div class="ksk-signin__field" @click="focusInput">
            <span class="ksk-signin__code">+65</span>
            <span class="ksk-signin__divider"></span>
            <div class="ksk-signin__display">
              <!-- Hidden input for actual data -->
              <input
                ref="realInput"
                type="tel"
                inputmode="none"
                v-model="input"
                @keydown="onKeyDown"
                @input="onInput"
                class="ksk-signin__input"
                autocomplete="off"
              />
              <!-- Display overlay -->
              <div class="display-overlay">
                <span v-for="(char, index) in displayChars" :key="index" :class="{ 'peek-char': shouldPeek(index) }">
                  {{ hideInput ? (shouldPeek(index) ? char : '•') : char }}
                </span>
              </div>
            </div>
            <button
              type="button"
              class="toggle-visibility"
              :aria-label="$t(hideInput ? 'kiosk.signIn.showNumber' : 'kiosk.signIn.hideNumber')"
              @click.stop="toggleVisibility"
            >
              <span v-if="hideInput" class="material-icons-round">visibility_off</span>
              <span v-else class="material-icons-round">visibility</span>
            </button>
          </div>

          <!-- Reserved error line: always takes its height so the keypad never jumps. -->
          <div class="ksk-signin__error" role="alert">
            <template v-if="snackbar === 'danger' && message">
              <span class="material-icons-round">error_outline</span>
              <span>{{ message }}</span>
            </template>
          </div>

          <KskNumericKeypad class="ksk-signin__keypad" clear-token="Clear" back-token="X" @press="handleKeyClick" />

          <KskButton
            class="ksk-signin__submit"
            size="md"
            block
            icon-right="arrow_forward"
            :disabled="isSubmitting || !input"
            @click="skip()"
          >{{ $t("kiosk.header.signIn") }}</KskButton>
          <button type="button" class="ksk-signin__guest" data-bs-dismiss="modal" @click="continueAsGuest">
            {{ $t("kiosk.signIn.continueGuest") }}
          </button>
          </template>
        </div>
      </KskModalShell>
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import MemberService from "@/services/member.service";
import { localStorageName } from "@/config/utils";
import EventBus from "vue3-eventbus";
import KskNumericKeypad from "./ui/KskNumericKeypad.vue";
import KskButton from "./ui/KskButton.vue";
import KskModalShell from "./ui/KskModalShell.vue";
import VueQRCodeComponent from "vue-qrcode-component";

// KSK065: how often / how long the kiosk waits for a QR sign-up to finish.
const SIGN_UP_POLL_MS = 3000;
const SIGN_UP_TIMEOUT_MS = 3 * 60 * 1000;

export default {
  name: "ModalSignInNew",
  emits: ["continue-guest"],
  components: {
    KskNumericKeypad,
    KskButton,
    KskModalShell,
    VueQRCodeComponent,
  },
  data() {
    return {
      snackbar: null,
      message: "",
      // One sign-in at a time. Each attempt spends a slot of the
      // login-phone-number rate limit, and the counter is shared by everyone at
      // this kiosk, so a second tap while the first call is still open costs a
      // real customer their turn.
      isSubmitting: false,
      merchantCode: "",
      branchCode: "",
      showFormLogin: false,
      input: "",
      memberType: 1,
      loginType: 1,
      branch: JSON.parse(
        localStorage.getItem(localStorageName("BRANCH_INFO"), "")
      ),
      step: 0,
      form2: {
        otp: "",
        phone_number: "",
        i_agree: true,
        passcode: "",
        confirmPasscode: "",
        is_empty_passcode: false,
        phone_code: ""
      },
      countryData: [],
      lastInputTimeout: null,
      showLastChar: false,
      hideInput: true,
      peekTimeout: null,
      peekIndex: -1,
      signUpPhone: "",
      signUpTimer: null,
      signUpTimeLeft: 0,
      signUpCountdown: null,
    };
  },
  computed: {
    signUpClock() {
      const t = Math.max(this.signUpTimeLeft, 0);
      return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
    },
    signUpUrl() {
      const base = (process.env.VUE_APP_MEMBER_SITE_URL || "").replace(/\/?$/, "/");
      return `${base}${this.merchantCode}/sign-in?phone=${encodeURIComponent(this.signUpPhone)}`;
    },
    displayChars() {
      return this.input.split('');
    },
    shouldPeek() {
      return (index) => {
        return this.hideInput && index === this.peekIndex;
      };
    }
  },
  mounted() {
    this.merchantCode = this.$store.state.merchantCode;
    this.branchCode = this.$store.state.branchCode;
    MemberService.getCountryDropdown().then(
      (response) => {
        if (response.data.result.isSuccess) {
          this.countryData = response.data.data;
          if (!this.form2.phone_code && this.countryData.length > 0) {
            this.form2.phone_code = this.countryData[0].value;
          }
        }
      }
    );
    // Focus input when modal is opened
    this.$nextTick(() => {
      this.focusInput();
    });
    // KSK065: Bootstrap 5 fires native events, which jQuery .on() never sees;
    // the field must hold focus on open so a scanned member QR lands in it.
    document.getElementById('modalSignIn')?.addEventListener('shown.bs.modal', this.focusInput);
    document.getElementById('modalSignIn')?.addEventListener('hidden.bs.modal', this.stopSignUp);
    // Listen for number keys from the physical keyboard to automatically focus input
    window.addEventListener('keydown', this.handleGlobalKeydown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleGlobalKeydown);
    document.getElementById('modalSignIn')?.removeEventListener('shown.bs.modal', this.focusInput);
    document.getElementById('modalSignIn')?.removeEventListener('hidden.bs.modal', this.stopSignUp);
    this.stopSignUp();
    if (this.peekTimeout) {
      clearTimeout(this.peekTimeout);
    }
  },
  methods: {
    changeMemberType(memberType) {
      $(".member-type .nav-link").removeClass("active");
      $(".member-type #nav_item_" + memberType).addClass("active");
      this.memberType = memberType;
    },
    changeLoginType(loginType) {
      $(".login-type .nav-link").removeClass("active");
      $(".login-type #nav_item_" + loginType).addClass("active");
      this.loginType = loginType;
    },
    async skip() {
      if (this.step !== 0 || this.isSubmitting) {
        return;
      }

      // KSK065: a scanned member QR may carry the country code ("6591234567").
      let phoneNumber = (this.input || "").replace(/\D/g, "");
      if (phoneNumber.length > 8 && phoneNumber.startsWith("65")) {
        phoneNumber = phoneNumber.slice(2);
      }
      this.input = phoneNumber;
      if (!phoneNumber) {
        return;
      }

      this.isSubmitting = true;
      try {
        this.form2.phone_number = phoneNumber;
        const data = {
          phone_number: phoneNumber,
          phone_code: "65"
        };

        const validated = await MemberService.validatePhone(data, this.merchantCode);
        if (!validated.data.result.isSuccess) {
          this.showError(validated.data.result.message || this.$t("kiosk.signIn.phoneInvalid"));
          return;
        }
        if (validated.data.data.is_first_login !== false) {
          this.startSignUp(phoneNumber);
          return;
        }

        await this.completeLogin(data);
      } catch (error) {
        // A rejected request used to disappear silently: the keypad simply did
        // nothing and the customer kept tapping, which is how a 429 turned into
        // a lockout nobody could see.
        this.showError(
          error?.response?.status === 429
            ? this.$t("kiosk.signIn.tooManyAttempts")
            : this.$t("kiosk.common.errorTryAgain")
        );
      } finally {
        this.isSubmitting = false;
      }
    },
    async completeLogin(data) {
      const loggedIn = await MemberService.loginPhoneNumber(data, this.merchantCode);
      if (!loggedIn.result.isSuccess) {
        this.showError(loggedIn.result.message || this.$t("kiosk.signIn.phoneInvalid"));
        return;
      }

      // Only now — a failed attempt keeps the digits on screen so the next tap
      // corrects a typo instead of re-sending an empty number.
      this.input = "";
      this.peekIndex = -1;

      if (this.$route.name === "LandingPageKiosk") {
        this.$router.push({
          name: "OrderPageKiosk",
          params: {
            merchant_code: this.merchantCode,
            branch_code: this.branchCode,
          },
        });
      } else {
        // SPA login: notify the host page to refresh member
        // state instead of doing a full page reload.
        EventBus.emit("LoginSuccess");
      }
      this.step = 0;
      $(".btn-hide-modal-signin").click();
    },
    // KSK065: show the sign-up QR and wait for the member to finish (agreed
    // to the terms and set a PIN), then sign them in here.
    startSignUp(phoneNumber) {
      this.stopSignUp();
      this.signUpPhone = phoneNumber;
      this.signUpTimeLeft = SIGN_UP_TIMEOUT_MS / 1000;
      this.signUpCountdown = setInterval(() => {
        this.signUpTimeLeft--;
        if (this.signUpTimeLeft <= 0) this.expireSignUp();
      }, 1000);
      this.signUpTimer = setTimeout(this.pollSignUp, SIGN_UP_POLL_MS);
    },
    stopSignUp() {
      if (this.signUpTimer) clearTimeout(this.signUpTimer);
      this.signUpTimer = null;
      if (this.signUpCountdown) clearInterval(this.signUpCountdown);
      this.signUpCountdown = null;
      this.signUpPhone = "";
      this.focusInput();
    },
    // KSK097: time is up — the customer has likely walked away, so close the
    // popup and leave the kiosk clean for the next person.
    expireSignUp() {
      this.stopSignUp();
      this.input = "";
      this.peekIndex = -1;
      $(".btn-hide-modal-signin").click();
    },
    async pollSignUp() {
      const phoneNumber = this.signUpPhone;
      if (!phoneNumber) return;
      const data = { phone_number: phoneNumber, phone_code: "65" };
      let registered = false;
      try {
        const validated = await MemberService.validatePhone(data, this.merchantCode);
        const state = validated.data.result.isSuccess ? validated.data.data : null;
        registered = !!state && state.is_first_login === false && state.is_empty_passcode === false;
      } catch (error) {
        // Network blip or rate limit: keep waiting until the timeout.
      }
      // Back / closed while the request was out.
      if (this.signUpPhone !== phoneNumber) return;
      if (!registered) {
        this.signUpTimer = setTimeout(this.pollSignUp, SIGN_UP_POLL_MS);
        return;
      }
      this.stopSignUp();
      this.isSubmitting = true;
      try {
        await this.completeLogin(data);
      } catch (error) {
        this.showError(this.$t("kiosk.common.errorTryAgain"));
      } finally {
        this.isSubmitting = false;
      }
    },
    showError(message) {
      this.snackbar = "danger";
      this.message = message;
      this.setTimeOutNotification();
    },
    handleKeyClick(key) {
      if (key === "Clear") {
        this.input = "";
        this.peekIndex = -1;
      } else if (key === "X") {
        this.input = this.input.slice(0, -1);
        this.peekIndex = -1;
      } else {
        this.input += key;
        this.triggerPeek(this.input.length - 1);
      }
      if (this.step === 1) {
        this.showLastChar = true;
        if (this.lastInputTimeout) clearTimeout(this.lastInputTimeout);
        this.lastInputTimeout = setTimeout(() => {
          this.showLastChar = false;
        }, 500);
      }
      this.focusInput(); // Focus again and set cursor to the end after each virtual key press
    },
    continueAsGuest() {
      this.input = "";
      this.peekIndex = -1;
      // The host page decides where a guest goes: Landing sends them on to
      // ordering, Order/Checkout have no listener and the modal just closes.
      this.$emit("continue-guest");
    },
    closeSnackbar() {
      this.snackbar = null;
    },
    onInput() {
      // Trigger peek effect for the last character when typing
      if (this.input.length > 0) {
        this.triggerPeek(this.input.length - 1);
      }
    },
    triggerPeek(index) {
      if (this.hideInput) {
        this.peekIndex = index;
        // Clear previous timeout
        if (this.peekTimeout) {
          clearTimeout(this.peekTimeout);
        }
        // Hide the character after 1 second
        this.peekTimeout = setTimeout(() => {
          this.peekIndex = -1;
        }, 1000);
      }
    },
    toggleVisibility() {
      this.hideInput = !this.hideInput;
      this.peekIndex = -1; // Reset peek when toggling visibility
      if (this.peekTimeout) {
        clearTimeout(this.peekTimeout);
        this.peekTimeout = null;
      }
      this.focusInput();
    },
    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
    onKeyDown(e) {
      // Only allow numbers, delete, and navigation keys
      if (
        !(
          (e.key >= '0' && e.key <= '9') ||
          e.key === 'Backspace' ||
          e.key === 'Delete' ||
          e.key === 'ArrowLeft' ||
          e.key === 'ArrowRight' ||
          e.key === 'Tab' ||
          e.key === 'Enter'
        )
      ) {
        e.preventDefault();
      }

    },
    focusInput() {
      this.$nextTick(() => {
        if (this.$refs.realInput) {
          this.$refs.realInput.focus();
          // Set cursor to the end of the string
          const len = this.input ? this.input.length : 0;
          this.$refs.realInput.setSelectionRange(len, len);
        }
      });
    },
    handleGlobalKeydown(e) {
      // If it's a number key and input is not focused, focus
      if ((e.key >= '0' && e.key <= '9') && document.activeElement !== this.$refs.realInput) {
        this.focusInput();
      }
      // If Enter is pressed and modal is open, submit. e.repeat filters the
      // keyboard's auto-repeat, which otherwise fired a sign-in per tick for as
      // long as the key was held down.
      if (e.key === 'Enter' && !e.repeat && document.activeElement === this.$refs.realInput) {
        e.preventDefault();
        this.skip();
      }
    },
  },
};
</script>

<style scoped>
.modal {
  overflow-y: hidden;
}
/* Compact sign-in: tighter shell padding than the 48px default. */
.modal :deep(.ksk-modal) {
  padding: 40px;
}

.ksk-signin {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.ksk-signin__close {
  position: absolute;
  top: -16px;
  right: -16px;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: var(--ksk-surface-muted);
  color: var(--ksk-text);
  cursor: pointer;
  transition: transform 80ms ease-out;
}
.ksk-signin__close .material-icons-round {
  font-size: 30px;
}
.ksk-signin__close:active {
  transform: scale(0.97);
}

.ksk-signin__icon {
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--ksk-primary-container);
  color: var(--ksk-primary);
}
.ksk-signin__icon .material-icons-round {
  font-size: 38px;
}

.ksk-signin__title {
  margin: var(--ksk-space-2) 0 0;
  font-family: var(--ksk-font);
  font-size: var(--ksk-title-size);
  line-height: var(--ksk-title-lh);
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--ksk-text);
}

.ksk-signin__caption {
  max-width: 520px;
  margin: var(--ksk-space-1) 0 var(--ksk-space-3);
  font-family: var(--ksk-font);
  font-size: 20px;
  line-height: 28px;
  color: var(--ksk-text-secondary);
}

/* 80px phone field: +65 chip | masked digits | eye toggle */
.ksk-signin__field {
  display: flex;
  align-items: center;
  gap: var(--ksk-space-2);
  width: 100%;
  height: 80px;
  padding: 0 var(--ksk-space-1) 0 var(--ksk-space-2);
  border: 2px solid transparent;
  border-radius: var(--ksk-radius-card);
  background: var(--ksk-surface-muted);
  cursor: text;
}
.ksk-signin__field:focus-within {
  border-color: var(--ksk-primary);
  background: var(--ksk-surface);
}

.ksk-signin__code {
  flex: none;
  padding: 8px 18px;
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-surface);
  box-shadow: var(--ksk-shadow-card);
  font-family: var(--ksk-font);
  font-size: var(--ksk-label-size);
  line-height: var(--ksk-label-lh);
  font-weight: 600;
  color: var(--ksk-text);
}

.ksk-signin__divider {
  flex: none;
  width: 2px;
  height: 40px;
  background: var(--ksk-border);
}

.ksk-signin__display {
  position: relative;
  flex: 1;
  min-width: 0;
  height: 100%;
}

.ksk-signin__input {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 36px;
  z-index: 2;
  opacity: 0;
}

.display-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 2px;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
  font-family: var(--ksk-font);
  font-size: 36px;
  line-height: 44px;
  font-weight: 700;
  letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums;
  color: var(--ksk-text);
}

.peek-char {
  font-weight: 800;
  transition: color 0.3s ease;
}

.toggle-visibility {
  flex: none;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ksk-text-secondary);
  cursor: pointer;
  z-index: 3;
}
.toggle-visibility .material-icons-round {
  font-size: 32px;
}

.ksk-signin__error {
  display: flex;
  align-items: center;
  gap: var(--ksk-space-1);
  width: 100%;
  min-height: 36px;
  padding: var(--ksk-space-1) var(--ksk-space-1) 0;
  font-family: var(--ksk-font);
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  font-weight: 600;
  color: var(--ksk-error);
  text-align: left;
}
.ksk-signin__error .material-icons-round {
  font-size: 24px;
}

.ksk-signin__keypad {
  width: 100%;
  margin-top: var(--ksk-space-1);
}


/* KSK065: sign-up QR view */
.ksk-signin__qr {
  display: flex;
  justify-content: center;
  margin-top: var(--ksk-space-2);
  padding: var(--ksk-space-2);
  border-radius: var(--ksk-radius-card);
  background: #ffffff;
  box-shadow: inset 0 0 0 1px var(--ksk-border);
}
.ksk-signin__waiting {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: var(--ksk-space-2) 0;
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  color: var(--ksk-text-secondary);
}
.ksk-signin__spinner {
  width: 22px;
  height: 22px;
  border: 3px solid var(--ksk-border);
  border-top-color: var(--ksk-primary);
  border-radius: 50%;
  animation: ksk-signin-spin 0.9s linear infinite;
}
.ksk-signin__expires {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: var(--ksk-space-2);
  font-family: var(--ksk-font);
  font-size: var(--ksk-label-size);
  line-height: var(--ksk-label-lh);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--ksk-text);
}
.ksk-signin__expires .material-icons-round {
  font-size: 28px;
  color: var(--ksk-primary);
}
@keyframes ksk-signin-spin {
  to {
    transform: rotate(360deg);
  }
}

/* KSK040: keypad tuned here only (ModalInputBuzzerNumber shares the keypad). */
.ksk-signin__keypad {
  grid-auto-rows: 88px;
}
.ksk-signin__keypad :deep(.ksk-keypad__key) {
  background: var(--ksk-surface-muted);
  border-color: transparent;
  font-size: 40px;
  line-height: 48px;
  font-weight: 600;
}

.ksk-signin__submit {
  margin-top: var(--ksk-space-3);
}

.ksk-signin__guest {
  margin-top: var(--ksk-space-1);
  padding: var(--ksk-space-1) var(--ksk-space-3);
  border: none;
  background: transparent;
  font-family: var(--ksk-font);
  font-size: var(--ksk-label-size);
  line-height: var(--ksk-label-lh);
  font-weight: 500;
  color: var(--ksk-text-secondary);
  cursor: pointer;
}
</style>
